import { IDENTITY_PATHS, type IdentityPath, identityAuthUrl } from '@words-happen/app-urls';
import { LandingScreen } from '@words-happen/screens';
import { type AuthProvider, type SocialProvider, useOncePerPageLoad } from '@words-happen/ui-components';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type DailyScheduleSource,
  type PresenceSource,
  stubDailyScheduleSource,
  stubPresenceSource,
  useDailySchedule,
  usePlayersCount,
} from '../../api';
import { ENABLED_AUTH_PROVIDERS } from '../../config';
import { type BrowserLocation, windowLocation } from '../../navigation';
import { ROUTES } from '../../routes';

export interface LandingPageProps {
  scheduleSource?: DailyScheduleSource;
  presenceSource?: PresenceSource;
  /** The identity app's origin. Defaults to this build's environment. */
  identityOrigin?: string;
  /** How sign-in and registration leave the app. Defaults to the window's location. */
  browserLocation?: BrowserLocation;
  /** The social providers that can be used; the others are shown "coming soon". Defaults to VITE_AUTH_PROVIDERS. */
  enabledProviders?: readonly SocialProvider[];
}

/**
 * /welcome: adapts the schedule and presence sources into LandingScreen's props, and sends sign-in and registration
 * to the identity app with the player's current URL as return_to.
 */
export const LandingPage: React.FC<LandingPageProps> = ({
  scheduleSource = stubDailyScheduleSource,
  presenceSource = stubPresenceSource,
  identityOrigin = IDENTITY_URL,
  browserLocation = windowLocation,
  enabledProviders = ENABLED_AUTH_PROVIDERS,
}) => {
  const navigate = useNavigate();
  const { schedule, refetch } = useDailySchedule(scheduleSource);
  const playersCount = usePlayersCount(presenceSource);
  const animateIntro = useOncePerPageLoad('landing-intro');

  const goToIdentity = (path: IdentityPath, provider?: AuthProvider) =>
    browserLocation.assign(identityAuthUrl(identityOrigin, path, { returnTo: browserLocation.href(), provider }));

  // Email registers with a password; the social providers start at sign-in, which creates the account if need be.
  const selectProvider = (provider: AuthProvider) =>
    provider === 'email' ? goToIdentity(IDENTITY_PATHS.register) : goToIdentity(IDENTITY_PATHS.signIn, provider);

  return (
    <LandingScreen
      playersCount={playersCount}
      dailyEndsAt={schedule?.endsAt}
      serverNow={schedule?.serverNow}
      animateIntro={animateIntro}
      enabledProviders={enabledProviders}
      onSignIn={() => goToIdentity(IDENTITY_PATHS.signIn)}
      onSelectProvider={selectProvider}
      onJustDropIn={() => navigate(ROUTES.play)}
      onOpenDaily={() => navigate(ROUTES.daily)}
      onDailyExpired={refetch}
    />
  );
};
